import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from '@bingads-webui-campaign-react/router';
import { Callout } from '@bingads-webui-react/callout';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { SummaryBarContainer } from '@bingads-webui-react/summary-bar';
import { SummaryBarItemsResizeGroup } from '@bingads-webui-campaign-react/summary-bar/src/components/summary-bar-items-resize-group';
import { SimpleSummaryBar } from '@bingads-webui-campaign-react/summary-bar';
import { Breadcrumb } from '@bingads-webui-react/breadcrumb';
import { CampaignOData } from '@bingads-webui-react/campaign-odata';
import { withScope } from '@bingads-webui-react/scope-service';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getUetDashboardSummaryBarStyle } from '../styles/summary-bar-style';
import { WebInsightsEditor } from './web-insights-editor';

export const Loader = (props) => {
  const {
    siteMap,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    backButtonText,
    backButtonUrl,
    updateAvailability,
    scope,
    i18n,
    isVisible,
    permissions,
    canEdit,
    ...others
  } = props;
  const uetDashboardPilotEnabled = permissions.dynamic.UETDashboard;
  const selectedParent = _.findWhere(siteMap, { selected: true });
  const trackingStatusMap = useMemo(() => ({
    Inactive: i18n.getString(_TL_('Inactive')),
    Unverified: i18n.getString(_TL_('Unverified')),
    Active: i18n.getString(_TL_('Active')),
  }), [i18n]);

  const [insightSettingOverride, setInsightSettingOverride] = useState({});
  const updateInsightSettingOverride = useCallback((tagIds, isInsightOn) => {
    setInsightSettingOverride(insight => ({
      ...insight,
      ..._.object(_.map(tagIds, tagId => [tagId, isInsightOn])),
    }));
  }, [setInsightSettingOverride]);

  useEffect(() => {
    const updateInsightSetting = event => updateInsightSettingOverride(event.detail.tagIds, true);

    document.addEventListener('turnOnInsightSummaryBar', updateInsightSetting);
    return () => {
      document.removeEventListener('turnOnInsightSummaryBar', updateInsightSetting);
    };
  }, [updateInsightSettingOverride]);

  useEffect(() => {
    updateAvailability(!_.isEmpty(selectedParent) && uetDashboardPilotEnabled);
  });

  if (selectedParent && uetDashboardPilotEnabled) {
    const defaultSummaryBarFallback = (
      <SimpleSummaryBar
        selectedParent={selectedParent}
        backButtonText={i18n.getString('Back to campaigns')}
        backButtonUrl={backButtonUrl}
        {...others}
      />
    );

    if (scope.values.aid) {
      scope.acid = props.serverContext.CurrentAccount.AdvertiserCustomerID;
    }
    if (scope.values.tagId && isVisible && permissions.dynamic.UETDashboard) {
      return (
        <CampaignOData
          call={({ currentAccount, currentCustomer }) => (scope.values.aid ? currentAccount.Tags.get({ top: 200 }) : currentCustomer.Tags.get({ top: 200 }))}
          escapeHatch={({ currentAccount, currentCustomer }) => (currentAccount || currentCustomer ? undefined : { value: defaultSummaryBarFallback })}
          fallback={defaultSummaryBarFallback}
          // If the list name call fails, show empty string.
          onError={() => defaultSummaryBarFallback}
        >
          {
            (response) => {
              const predicate = item => item.Id === scope.values.tagId;

              const backButtonProps = {
                url: `/campaign/vnext/uettag?uid=${scope.values.uid}&cid=${scope.values.cid}&aid=${scope.values.aid}`,
                buttonText: i18n.getString(_TL_('Back to All tags')),
              };

              if (response.value && _.find(response.value, predicate)) {
                const list = _.find(response.value, predicate);
                const renderBreadCrumb = () => {
                  const items = [{
                    key: 'uet-dashboard-tag',
                    text: i18n.getString(_TL_('UET tag')),
                  }, {
                    key: 'uet-dashboard-tag-name',
                    text: list.Name,
                  }];

                  return (
                    <div className="summary-bar-breadcrumb">
                      <Breadcrumb items={items} />
                    </div>
                  );
                };

                const resizeGroup = [{
                  label: i18n.getString(_TL_('Tag ID')),
                  className: 'summary-bar-tag-id',
                  renderContent: () => list.Id,
                  ariaLabel: i18n.getString(_TL_('Tag ID')),
                  canEdit: false,
                }, {
                  label: i18n.getString(_TL_('Tracking Status')),
                  className: 'summary-bar-tracking-status',
                  renderContent: () => (!list.IsReadOnly ? (trackingStatusMap[list.TrackingStatus] || list.TrackingStatus) : trackingStatusMap.Active),
                  ariaLabel: i18n.getString(_TL_('Tracking Status')),
                  canEdit: false,
                }, {
                  label: i18n.getString(_TL_('Goals')),
                  className: 'summary-bar-goals',
                  renderContent: () => list.GoalsCount,
                  ariaLabel: i18n.getString(_TL_('Goals')),
                  canEdit: false,
                }, {
                  label: i18n.getString(_TL_('Audiences')),
                  className: 'summary-bar-audiences',
                  renderContent: () => list.AudiencesCount,
                  ariaLabel: i18n.getString(_TL_('Audiences')),
                  canEdit: false,
                }];

                if (permissions.dynamic.UETTagInsights) {
                  resizeGroup.push({
                    label: i18n.getString(_TL_('Insights')),
                    className: 'summary-bar-insights',
                    renderContent: () => {
                      const isInsightOn = !_.isUndefined(insightSettingOverride[list.Id]) ? insightSettingOverride[list.Id] : list.IsWebInsightsEnabled;
                      return (
                        <div className="edit-icon">
                          <span className="web-insights">{isInsightOn ? i18n.getString(_TL_('Enabled')) : i18n.getString(_TL_('Disabled'))}</span>
                          <span className="glyphicon glyphicon-pencil" />
                        </div>
                      );
                    },
                    ariaLabel: i18n.getString(_TL_('Insights')),
                    canEdit,
                    Editor: Callout,
                    getEditorProps: (show, closeEditor) => ({
                      title: i18n.getString(_TL_('Edit your Insights setting')),
                      content: (
                        <WebInsightsEditor
                          i18n={i18n}
                          scope={scope}
                          tag={list}
                          isInsightOn={!_.isUndefined(insightSettingOverride[list.Id]) ? insightSettingOverride[list.Id] : list.IsWebInsightsEnabled}
                          onSave={(newIsInsightOn) => {
                            // eslint-disable-next-line no-param-reassign
                            list.IsWebInsightsEnabled = newIsInsightOn;
                            updateInsightSettingOverride([list.Id], newIsInsightOn);
                            closeEditor();
                          }}
                          onCancel={() => {
                            closeEditor();
                          }}
                        />
                      ),
                      show,
                      onClose: closeEditor,
                      closeButton: true,
                      target: null,
                      placement: 'bottom',
                      popoverId: 'summary-bar-insights-editor',
                      popoverClassName: 'summary-bar-insights-editor',
                      closeButtonAriaLabel: i18n.getString(_TL_('Close')),
                      focusLockContent: true,
                    }),
                  });
                }

                return (
                  <SummaryBarContainer
                    texts={{ ariaLabel: i18n.getString(_TL_('Summary bar')) }}
                    backButtonProps={backButtonProps}
                    breadcrumb={renderBreadCrumb()}
                    summaryBar={<SummaryBarItemsResizeGroup summaryBarItems={resizeGroup} scope={scope} siteMap={siteMap} />}
                  />
                );
              }

              return defaultSummaryBarFallback;
            }
          }
        </CampaignOData>
      );
    }

    return defaultSummaryBarFallback;
  }

  return null;
};

Loader.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.isRequired,
  }).isRequired,
  siteMap: siteMapPropType.isRequired,
  backButtonText: PropTypes.string,
  backButtonUrl: PropTypes.string,
  updateAvailability: PropTypes.func,
  scope: PropTypes.shape({
    acid: PropTypes.number,
    values: PropTypes.shape({
      cid: PropTypes.number,
      uid: PropTypes.number,
      aid: PropTypes.number,
      tagId: PropTypes.number,
    }).isRequired,
  }).isRequired,
  isVisible: PropTypes.bool.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      UETDashboard: PropTypes.bool.isRequired,
      UETTagInsights: PropTypes.bool.isRequired,
    }),
  }).isRequired,
};

Loader.defaultProps = {
  backButtonText: null,
  backButtonUrl: null,
  updateAvailability: _.noop,
};

export const UetDashboardLoader = withDefaultStyles(withServerContext(withScope(withRouter(React.memo(Loader)))), getUetDashboardSummaryBarStyle);
